import { Driver, PopoverDOM } from 'driver.js';

/**
 * Simulates a right-click (context menu) event on an element with the specified data-cell-id.
 * @param {string} identifier - The value of the data-cell-id attribute of the target element.
 */
export const simulateContextMenu = (identifier: string) => {
  const element = document.querySelector(identifier);

  if (element) {
    const rect = element.getBoundingClientRect();

    const event = new MouseEvent('contextmenu', {
      bubbles: true,
      view: window,
      clientX: rect.left + window.scrollX,
      clientY: rect.top + window.scrollY,
      button: 2, // Right-click
    });

    element.dispatchEvent(event);
  } else {
    console.warn(`Element"${identifier}" not found.`);
  }
};

/**
 * Simulates a click event on a menu item with the specified ID.
 * @param {string} identifier - The ID of the menu item to be clicked.
 */
export const simulateClick = (identifier: string) => {
  const item = document.querySelector(identifier);

  if (item) {
    const rect = item.getBoundingClientRect();

    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + window.scrollX,
      clientY: rect.top + window.scrollY,
      view: window,
    });

    item.dispatchEvent(clickEvent);
  } else {
    console.warn(`Item "${identifier}" not found.`);
  }
};

/**
 * Simulates a double-click event on a menu item with the specified ID.
 * @param {string} identifier - The ID of the menu item to be double-clicked.
 */
export const simulateDoubleClick = (identifier: string) => {
  const item = document.querySelector(identifier);

  if (item) {
    const rect = item.getBoundingClientRect();

    const doubleClickEvent = new MouseEvent('dblclick', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + window.scrollX,
      clientY: rect.top + window.scrollY,
      view: window,
    });

    item.dispatchEvent(doubleClickEvent);
  } else {
    console.warn(`Item "${identifier}" not found.`);
  }
};

/**
 * Simulates a mouseover event on a menu item with the specified ID.
 * @param {string} identifier - The ID of the menu item to trigger mouseover on.
 */
export const simulateMouseOver = (identifier: string) => {
  const item = document.querySelector(identifier);

  if (item) {
    const rect = item.getBoundingClientRect();

    // Simulate mouseover event
    const mouseOverEvent = new MouseEvent('mouseover', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + window.scrollX,
      clientY: rect.top + window.scrollY,
      view: window,
    });

    item.dispatchEvent(mouseOverEvent);
  } else {
    console.warn(`Item "${identifier}" not found.`);
  }
};

/**
 * Simulates a mouseout event on a menu item with the specified ID.
 * @param {string} identifier - The ID of the menu item to trigger mouseout on.
 */
export const simulateMouseOut = (identifier: string) => {
  const item = document.querySelector(identifier);

  if (item) {
    const rect = item.getBoundingClientRect();

    // Simulate mouseout event
    const mouseOutEvent = new MouseEvent('mouseout', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + window.scrollX,
      clientY: rect.top + window.scrollY,
      view: window,
    });

    item.dispatchEvent(mouseOutEvent);
  } else {
    console.warn(`Item "${identifier}" not found.`);
  }
};

/**
 * Adds a custom button to the footer of the provided popover, which opens a link or multiple links in new tabs.
 *
 * @param {Object} params - The parameters for the function.
 * @param {PopoverDOM} params.popover - The popover DOM element to which the button will be added.
 * @param {string | string[]} params.link - The URL or an array of URLs that the button will open when clicked.
 * @param {string} [params.btnTitle='Go to FAQ'] - The title of the button. Defaults to "Go to FAQ" if not provided.
 * @param {Driver} params.driverInstance - The driver instance used to move to the next step in the guided tour.
 *
 * @returns {void} This function does not return a value.
 *
 * @example
 * // Single link usage
 * driver.defineSteps([
 *   {
 *     element: '#step1',
 *     popover: {
 *       title: 'Step 1',
 *       description: 'This is the first step.',
 *       onPopoverRender: (popover) =>
 *         addFAQButton({
 *           popover,
 *           link: 'https://servoitsolutions.ph/support/kb/faq.php?cid=222',
 *           driverInstance: driverObj,
 *         }),
 *     },
 *   },
 * ]);
 *
 * @example
 * // Multiple links usage
 * driver.defineSteps([
 *   {
 *     element: '#step1',
 *     popover: {
 *       title: 'Step 1',
 *       description: 'This is the first step.',
 *       onPopoverRender: (popover) =>
 *         addFAQButton({
 *           popover,
 *           link: [
 *             'https://servoitsolutions.ph/support/kb/faq.php?cid=222',
 *             'https://servoitsolutions.ph/support/kb/faq.php?cid=333',
 *           ],
 *           driverInstance: driverObj,
 *           btnTitle: 'View All FAQs',
 *         }),
 *     },
 *   },
 * ]);
 */
export const addFAQButton = ({
  popover,
  link,
  driverInstance,
  btnTitle,
}: {
  popover: PopoverDOM;
  link: string | string[];
  driverInstance: Driver;
  btnTitle?: string;
}): void => {
  const customButton = document.createElement('button');
  customButton.innerText = btnTitle || 'Go to FAQ';
  popover.footerButtons.appendChild(customButton);

  customButton.addEventListener('click', () => {
    if (Array.isArray(link)) {
      // Open each link in a new tab
      link.forEach((targetLink) => {
        if (targetLink) {
          window.open(targetLink, '_blank', 'noopener');
        }
      });
    } else {
      // Open the single link in a new tab
      window.open(link, '_blank', 'noopener');
    }

    driverInstance.moveNext();
  });
};
